import css from './Desktop.module.scss'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import classNames from 'classnames'

import { ITicket } from 'meta/backendControlled/structure'
import { CurrencySymbol } from 'meta/currency'
import { RouteParams } from 'meta/routes'
import { convertAndFormat, getCodeFromSymbol } from 'helpers/currency/converter'
import { Dates } from 'utils/dates'
import { convertDotsToCommas } from 'utils/string'

import EventFeesButton from 'components/BackendControlled/Component/common/EventFeesButton'
import { Grid, GridColumn } from 'components/Layout/Grid'
import Img from 'components/Media/Img'
import { useActiveCurrency } from 'hooks/useActiveCurrency'
import { TicketRules } from 'meta/backendControlled/structure/ticket'

interface IProps {
  data: ITicket
  isGroupTicket?: boolean
  // eslint-disable-next-line @typescript-eslint/ban-types
  callback: Function
}

const Desktop: React.FC<IProps> = (props) => {
  const { callback, data, isGroupTicket } = props
  const { currency, id, title, orderCodeDescription, priceNumber, status, availableUntil, sector, details, saleRules } = data

  const ticketTitle = sector && sector.name ? `${sector.name} - ${title}` : title

  const transactionMinQuantity =
    saleRules?.find((x) => x.rule == TicketRules.transactionMinQuantity)?.value ?? null

  const transactionMaxQuantity =
    saleRules?.find((x) => x.rule == TicketRules.transactionMaxQuantity)?.value ?? null
  
  const { t } = useTranslation()
  const { activeCurrency } = useActiveCurrency()
  const { codeUrl } = useParams<RouteParams['Event']>()

  const [isOpen, setOpen] = useState(false)

  const disabled = status === 'soldOut' || status === 'comingSoon'

  const toggleTicketOpen = () => {
    setOpen(!isOpen)
  }

  const execCallback = () => {
    if (status === 'soldOut' || status === 'comingSoon') {
      return
    }

    callback(id, orderCodeDescription)
  }

  const getAvailableUntil = () => {
    return Dates.format(new Date(availableUntil), t('dates:common:ticket'))
  }

  const getTitle = () => {
    if (ticketTitle.length > 40 && isOpen) {
      return `${ticketTitle.substr(0, 40)}...`
    }

    return ticketTitle
  }

  const eventCurrencyCode = getCodeFromSymbol(currency as CurrencySymbol)

  const convertedPrice = convertAndFormat(priceNumber, eventCurrencyCode, activeCurrency)

  return (
    <Grid className={css.eventTicketContainer}>
      <GridColumn gridColumnEnd={17}>
        <div
          className={classNames(css.event_body_tickets_ticket_desktop_container, {
            [css.disabled]: disabled,
            [css.isGroup]: isGroupTicket 
          })}
          onClick={execCallback}
        >
          <div onMouseOver={toggleTicketOpen} onMouseOut={toggleTicketOpen} className={css.whitebox_container}>
            <div className={css.ticket_image}>
              <Img src={`/tickets/ticket_${isGroupTicket ? "group" : "logo"}_placeholder.svg`} />
            </div>

            <div className={classNames(css.ticket_info, { [css.grey]: disabled })}>
              <h3 className={css.title}>{getTitle()}</h3>
              <div>
                <div className={css.details}>{details}</div>
                {availableUntil && <div>{`${t('event:purchase_available_until')} ${getAvailableUntil()}`}</div>}
              </div>
              {isGroupTicket && (transactionMinQuantity || transactionMaxQuantity) && <div className={css.qtyLimits}>
                {transactionMinQuantity && <span>{t('event:groups_min')}</span>}
                {transactionMinQuantity && <span className={css.heavy}>{`${String(transactionMinQuantity)} ${t('event:groups_people')}`}</span>}
                {transactionMinQuantity && transactionMaxQuantity && <span>{` - `}</span>}
                {transactionMaxQuantity && <span>{t('event:groups_max')}</span>}
                {transactionMaxQuantity && <span className={css.heavy}>{`${String(transactionMaxQuantity)} ${t('event:groups_people')}`}</span>}
              </div>}
            </div>

            <div className={classNames(css.ticket_price_box, { [css.grey]: disabled })}>
              {
                <h2 className={classNames(css.price, { [css.hide]: status === 'comingSoon' })}>{`${convertDotsToCommas(
                  convertedPrice
                )}`}</h2>
              }
              {status !== 'comingSoon' && <EventFeesButton codeUrl={codeUrl as string} />}
              
              {isGroupTicket && <div className={css.perPerson}>{t('event:groups_per_person')}</div>}
            </div>
          </div>

          <div className={css.ticket_inner_container}>
            {status === 'active' && <Img src={'/tickets/ticket_tip_green.svg'} />}
            {status === 'exhaust' && <Img src={'/tickets/ticket_tip_yellow.svg'} />}
            {status === 'soldOut' && <Img src={'/tickets/ticket_tip_red.svg'} />}
            {status === 'comingSoon' && <Img src={'/tickets/ticket_tip_purple.svg'} />}

            <div className={css.text}>
              {status === 'active' && t('event:ticket_available')}
              {status === 'exhaust' && t('event:ticket_last_places_remaining')}
              {status === 'soldOut' && t('event:ticket_soldout')}
              {status === 'comingSoon' && t('event:ticket_tba')}
            </div>
          </div>
        </div>
      </GridColumn>
    </Grid>
  )
}

export default Desktop
